import { gql } from '@apollo/client'
import { useLazyQuery } from '@apollo/client/react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { SEARCH_PAGE_CITY_MAP, SearchPageCity } from 'src/constants/searchPages'
import { useAuthProvider } from 'src/context/AuthProvider'
import useLogger from 'src/context/LoggerProvider/useLogger'
import {
  IAddress,
  ICoordinates,
  IGooglePlaceDetails,
  IQuery,
  IQueryGoogleAddressArgs
} from '../types/graphql'

const GET_GOOGLE_ADDRESS = gql`
  query GetGoogleAddress($lat: Float!, $lng: Float!) {
    googleAddress(lat: $lat, lng: $lng) {
      street1
      street2
      neighborhood
      colonia
      city
      state
      zipcode
      country
    }
  }
`

const NEAR_USER = 'NEAR_USER'

const BROWSER_GEO_ZOOM = 13
const IP_GEO_ZOOM = 12

/** (0, 0) is often returned by browsers when geolocation fails; backend rejects it. */
const areValidCoordinates = (lat: number, lng: number): boolean =>
  (lat !== 0 || lng !== 0) &&
  lat >= -90 &&
  lat <= 90 &&
  lng >= -180 &&
  lng <= 180

type IUserLocation = {
  /** Best available location: browser geo > IP geo. */
  placeDetails: IGooglePlaceDetails | undefined
  /** Best available center coordinates: browser geo > IP geo. */
  center: ICoordinates | undefined
  /** Recommended zoom based on location accuracy (14 GPS, 12 IP). */
  zoom: number | undefined
  /** Raw browser geolocation — undefined when denied/unavailable. */
  browserLocation: IGooglePlaceDetails | undefined
  /** IP-based city-level center — undefined for non-MX IPs. */
  ipCenter: ICoordinates | undefined
  /** True while browser geolocation is still resolving. */
  loading: boolean
  /** Async getter: tries browser prompt first, falls back to IP. */
  getUserLocation: () => Promise<IGooglePlaceDetails | undefined>
  /** Reverse-geocoded address (only when browser geo succeeds). */
  userAddress?: IAddress | undefined
  /** Closest SearchPageCity to user — falls back to Monterrey. */
  closestCity: SearchPageCity
}

/**
 * Unified user location hook.
 *
 * Combines browser geolocation (high accuracy, may be denied) with
 * IP-based geolocation from the server (city-level, always available
 * for MX IPs). Browser geo always takes priority.
 *
 * When called inside AuthProvider (before the context is provided),
 * `useAuthProvider()` returns the default context (`ipInfo: null`),
 * so the IP fallback gracefully does not apply.
 */
const useUserLocation = (dontWaitForConsent = false): IUserLocation => {
  const [consent, setConsent] = useState<boolean>(dontWaitForConsent ?? false)
  const [browserLocation, setBrowserLocation] = useState<IGooglePlaceDetails>()
  const [loadingPosition, setLoadingPosition] = useState<boolean>(
    true && dontWaitForConsent
  )
  const logger = useLogger()
  const { ipInfo, currentUser } = useAuthProvider()

  const ipCenterLat = ipInfo?.center?.lat
  const ipCenterLng = ipInfo?.center?.lng
  const ipCenter: ICoordinates | undefined = useMemo(
    () =>
      ipCenterLat && ipCenterLng
        ? { lat: ipCenterLat, lng: ipCenterLng }
        : undefined,
    [ipCenterLat, ipCenterLng]
  )

  const lastKnownLat = currentUser?.lastKnownLocation?.center?.lat
  const lastKnownLng = currentUser?.lastKnownLocation?.center?.lng
  const lastKnownCenter: ICoordinates | undefined = useMemo(
    () =>
      lastKnownLat && lastKnownLng
        ? { lat: lastKnownLat, lng: lastKnownLng }
        : undefined,
    [lastKnownLat, lastKnownLng]
  )

  const fallbackCenter = ipCenter ?? lastKnownCenter

  /**
   * Opportunistic probe: skip the consent gate when geolocation was already
   * granted. `permissions.query` is environment-dependent beyond mere
   * existence — browsers that don't implement the `geolocation` descriptor
   * reject, and scripts that monkey-patch `navigator.permissions.query`
   * (extensions, headless-detection shims) can unbind it, throwing
   * `TypeError: Illegal invocation`. This is a best-effort optimisation, so a
   * failure must stay silent: consent remains false and the hook falls back to
   * IP geolocation exactly as it does for a user who never granted access.
   */
  useEffect(() => {
    if (!window.navigator.permissions) return
    const checkPermission = async () => {
      try {
        const permission = await navigator.permissions.query({
          name: 'geolocation'
        })
        if (permission.state === 'granted') {
          setConsent(true)
        }
      } catch {
        // Intentionally ignored — see the note above; IP geolocation takes over.
      }
    }
    checkPermission()
  }, [])

  /* Near User on Load */
  useEffect(() => {
    if (!consent) return

    if (!window.navigator.geolocation) {
      setBrowserLocation(undefined)
      setLoadingPosition(false)
      return
    }

    // Fallback
    let hasBeenSet = false
    const handler = setTimeout(() => {
      if (!hasBeenSet) {
        hasBeenSet = true
        setBrowserLocation(undefined)
        setLoadingPosition(false)
      }
    }, 10000)

    setLoadingPosition(true)

    window.navigator.geolocation.getCurrentPosition(
      ({ coords: { latitude, longitude } }) => {
        hasBeenSet = true
        if (!areValidCoordinates(latitude, longitude)) {
          setBrowserLocation(undefined)
        } else {
          setBrowserLocation({
            lat: latitude,
            lng: longitude,
            placeId: NEAR_USER
          })
        }
        setLoadingPosition(false)
      },
      (error) => {
        logger.error('Error retrieving geolocation', error)
        if (!hasBeenSet) {
          hasBeenSet = true
          setBrowserLocation(undefined)
          setLoadingPosition(false)
        }
      }
    )

    return () => {
      clearTimeout(handler)
    }
  }, [logger, consent])

  const browserLat = browserLocation?.lat
  const browserLng = browserLocation?.lng
  const fallbackLat = fallbackCenter?.lat
  const fallbackLng = fallbackCenter?.lng

  /* Near User on Action — tries browser first, falls back to IP / last known */
  const getUserLocation = useCallback(async () => {
    if (browserLocation) return browserLocation

    const placeD = await getGeolocation()
    if (placeD) {
      setBrowserLocation(placeD)
      return placeD
    }

    if (fallbackLat && fallbackLng) {
      return { lat: fallbackLat, lng: fallbackLng, placeId: NEAR_USER }
    }
    return undefined
  }, [browserLat, browserLng, fallbackLat, fallbackLng]) // eslint-disable-line react-hooks/exhaustive-deps

  const placeDetails: IGooglePlaceDetails | undefined = useMemo(
    () =>
      browserLocation ??
      (fallbackLat && fallbackLng
        ? { lat: fallbackLat, lng: fallbackLng, placeId: NEAR_USER }
        : undefined),
    [browserLat, browserLng, fallbackLat, fallbackLng] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const center: ICoordinates | undefined = useMemo(
    () =>
      browserLat && browserLng
        ? { lat: browserLat, lng: browserLng }
        : fallbackCenter,
    [browserLat, browserLng, fallbackLat, fallbackLng] // eslint-disable-line react-hooks/exhaustive-deps
  )

  /**
   * Graphql Query to get the user location
   */
  const [
    getGoogleAddress,
    { data, error: addressError, loading: loadingAddress }
  ] = useLazyQuery<Pick<IQuery, 'googleAddress'>, IQueryGoogleAddressArgs>(
    GET_GOOGLE_ADDRESS
  )

  useEffect(() => {
    if (addressError) {
      logger.error('Error fetching google address.', addressError)
    }
  }, [addressError, logger])

  useEffect(() => {
    if (loadingAddress) return
    if (
      browserLat &&
      browserLng &&
      !data &&
      areValidCoordinates(browserLat, browserLng)
    ) {
      getGoogleAddress({ variables: { lat: browserLat, lng: browserLng } })
    }
  }, [browserLat, browserLng, data, loadingAddress, getGoogleAddress])

  const centerLat = center?.lat
  const centerLng = center?.lng
  const closestCity = useMemo(() => {
    if (!centerLat || !centerLng) return SearchPageCity.Monterrey
    const [closest] = Object.entries(SEARCH_PAGE_CITY_MAP)
      .filter(([, { center: c }]) => c)
      .map(([key, { center: c }]): [SearchPageCity, number] => [
        key as SearchPageCity,
        Math.sqrt((c!.lat - centerLat) ** 2 + (c!.lng - centerLng) ** 2)
      ])
      .sort((a, b) => a[1] - b[1])
    return closest[0]
  }, [centerLat, centerLng])

  const zoom = browserLat
    ? BROWSER_GEO_ZOOM
    : fallbackLat
      ? IP_GEO_ZOOM
      : undefined

  return {
    placeDetails,
    center,
    zoom,
    browserLocation,
    ipCenter,
    loading: loadingPosition,
    getUserLocation,
    userAddress: data?.googleAddress,
    closestCity
  }
}

function getGeolocation(): Promise<IGooglePlaceDetails | undefined> {
  return new Promise((resolve) => {
    if (!window.navigator.geolocation) return resolve(undefined)

    window.navigator.geolocation.getCurrentPosition(
      ({ coords: { latitude, longitude } }) => {
        if (!areValidCoordinates(latitude, longitude)) {
          resolve(undefined)
        } else {
          resolve({ lat: latitude, lng: longitude, placeId: NEAR_USER })
        }
      },
      () => {
        resolve(undefined)
      }
    )
  })
}

export default useUserLocation
